import React from 'react';
import gql from 'graphql-tag';
import { useQuery } from '@apollo/react-hooks';
import format from 'date-fns/format';

import Tooltip from '@material-ui/core/Tooltip';
import HistoryIcon from '@material-ui/icons/History';
import { withStyles } from '@material-ui/core/styles';

import { findingLabel } from './AiReviewFindings';

export const AI_REVIEW_FIELD_HISTORY_QUERY = gql`
  query aiReviewFieldHistory($productId: ID!) {
    aiReviewFieldHistory(productId: $productId)
  }
`;

export interface IAiReviewFieldHistoryEntry {
  runId: string;
  startedAt: string;
  userName: string | null;
  model: string;
  promptVersion: number | null;
  status: string | null;
  confidence: number | null;
  reason: string | null;
  findings?: { code: string; severity: string; legalBasis: string | null }[] | null;
}

// Field name to its verdicts in the past runs, newest first.
export type TAiReviewFieldHistory = Record<string, IAiReviewFieldHistoryEntry[]>;

interface IAiReviewFieldHistoryLoaderProps {
  productId: string;
  // Changes when a new review is run, to load the history again.
  refreshKey?: string | null;
  onLoad: (history: TAiReviewFieldHistory) => void;
}

// Superuser only: loads the field history of the AI review and hands it to the panel.
export const AiReviewFieldHistoryLoader = ({ productId, refreshKey, onLoad }: IAiReviewFieldHistoryLoaderProps) => {
  const { data, refetch } = useQuery<{ aiReviewFieldHistory: TAiReviewFieldHistory }>(AI_REVIEW_FIELD_HISTORY_QUERY, {
    variables: { productId },
    fetchPolicy: 'cache-and-network',
  });
  const firstKey = React.useRef(refreshKey);
  React.useEffect(() => {
    if (refreshKey && refreshKey !== firstKey.current) refetch();
  }, [refreshKey, refetch]);
  React.useEffect(() => {
    if (data?.aiReviewFieldHistory) onLoad(data.aiReviewFieldHistory);
  }, [data, onLoad]);
  return null;
};

// The same dark tooltip as the field history of the product form (FieldHistoryIcon).
const HistoryTooltip = withStyles(() => ({
  tooltip: {
    backgroundColor: '#000000',
    color: '#ffffff',
    maxWidth: 600,
    fontSize: '12px',
    border: '1px solid #334155',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.5), 0 4px 6px -2px rgba(0, 0, 0, 0.3)',
    padding: '8px 12px',
    borderRadius: '8px',
  },
}))(Tooltip);

const REASON_LENGTH = 140;
const shorten = (text: string) => (text.length > REASON_LENGTH ? `${text.slice(0, REASON_LENGTH - 1)}…` : text);

interface IAiReviewFieldHistoryIconProps {
  entries: IAiReviewFieldHistoryEntry[];
  // The run shown in the panel, marked in the list.
  currentRunId?: string | null;
}

// How a field was judged in the past AI review runs, like the history of a field in the form.
export const AiReviewFieldHistoryIcon = ({ entries, currentRunId }: IAiReviewFieldHistoryIconProps) => (
  <HistoryTooltip
    interactive
    placement="top-start"
    enterDelay={0}
    leaveDelay={300}
    title={
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }} data-testid="ai-review-field-history">
        {entries.map(entry => (
          <div key={entry.runId} style={{ lineHeight: '1.4' }}>
            <div style={{ whiteSpace: 'nowrap' }}>
              <span style={{ color: '#94a3b8' }}>{format(new Date(entry.startedAt), 'yyyy-MM-dd HH:mm')}</span>
              {entry.userName && <span style={{ color: '#fbbf24' }}>{` (${entry.userName})`}</span>}
              <span style={{ color: '#60a5fa' }}>
                {` [${entry.model}${entry.promptVersion !== null ? ` · v${entry.promptVersion}` : ''}]`}
              </span>
              <span style={{ color: '#94a3b8' }}>: </span>
              <span style={{ color: entry.status === 'failed' ? '#f87171' : '#4ade80', fontWeight: 500 }}>
                {entry.status || '—'}
              </span>
              {entry.confidence !== null && <span style={{ color: '#ffffff' }}>{` ${entry.confidence}%`}</span>}
              {entry.runId === currentRunId && <span style={{ color: '#94a3b8', fontStyle: 'italic' }}> (shown now)</span>}
            </div>
            {!!entry.findings?.length && (
              <div style={{ color: '#fbbf24', whiteSpace: 'normal' }}>{entry.findings.map(finding => findingLabel(finding.code)).join(' · ')}</div>
            )}
            {entry.reason && <div style={{ color: '#cbd5e1', whiteSpace: 'normal' }}>{shorten(entry.reason)}</div>}
          </div>
        ))}
      </div>
    }
  >
    <HistoryIcon
      aria-hidden={false}
      aria-label="AI review history of this field"
      data-testid="ai-review-field-history-icon"
      style={{ color: '#94a3b8', fontSize: '18px', flexShrink: 0, marginRight: 4, cursor: 'default' }}
    />
  </HistoryTooltip>
);
